import React, {useState} from 'react';
import i18n from '../../i18n';
import Button from '@material-ui/core/Button';
import Icon from '@material-ui/core/Icon';
import FormField from '../Form/FormField';
import Grid from '@material-ui/core/Grid/Grid';
import DeleteButton from '../Form/DeleteButton';
import FormSelect from '../Form/FormSelect';
import FormAutocompleteGQL from '../Form/FormAutocompleteGQL';
import contactQueries from "../../queries/contactQueries";
import buildingQueries from "../../queries/buildingQueries";
import {getSelectOptionEntity, calculateDate} from '../Form/utils';
import costCenterQueries from "../../queries/costCenterQueries";
import Divider from "@material-ui/core/Divider";
import {Typography} from "@material-ui/core";
import FormCheck from "../Form/FormCheck";

/**
 * @param values
 * @param touched
 * @param errors
 * @param handleChange
 * @param handleBlur
 * @param setFieldValue
 * @param classes
 * @param formUrl
 * @param isSubmitting
 * @param props
 */
const FormFields = ({
    values,
    touched,
    errors,
    handleChange,
    handleBlur,
    setFieldValue,
    classes,
    formUrl,
    isSubmitting,
    ...props
}) => {

    // if there is a parent building get parent values to prefill form fields
    [
        'zip',
        'city',
        'cost_center_id',
        'cost_center',
        'builder_id',
        'builder',
        'contact_id',
    ].map(key => {
        values[key] = values[key] || (values.parent ? values.parent[key] : '');
    })

    // graphql expects type boolean; 0 or 1 cannot represent value as boolean
    // -> transform numbers to type boolean
    values.completed = Boolean(values.completed);

    const [contacts,setContacts] = useState(values.builder ? values.builder.children : []);
    const keyContacts = values.builder && values.builder.key_contacts ? values.builder.key_contacts.split(',') : [] ;
    const relation = !!values.parent;
    const [acceptance,setAcceptance] = useState(values.acceptance ? values.acceptance.substring(0, 10) : '');
    const [warrantyEnd,setWarrantyEnd] = useState(values.warranty_period_end ? values.warranty_period_end.substring(0, 10) : '');
    const management = values.management || {};

    let managementOptions = [];
    for (let [k, v] of Object.entries(management)) {
        managementOptions[k] = {
            label: v.first_name + ' ' + v.last_name,value: v.id
        };
    }

    const [personnel,setPersonnel] = useState(values.personnel || { building_id: values.id || null , manager_id:null, foreman_id:null, substitute_id:null});

    const calculateWarrantyEnd = (date) =>{
        let calculated = calculateDate(date,
            'add',
            5,
            'years',
            'warranty_period_end');
        setWarrantyEnd(calculated);
        setFieldValue('warranty_period_end',calculated);
    }

    const handleManagementChange = (value,type) => {
        if(type) {
            setFieldValue('personnel',{...personnel,[type+'_id']: value });
            setPersonnel({...personnel,[type+'_id']: value });
        }
    }

    return (
        <React.Fragment>

            <Grid container spacing={8}>
                {(values.parent || (values.children && values.children.length === 0)) && (
                    <React.Fragment>
                        <Grid item xs={12}>
                            <FormAutocompleteGQL
                                query={buildingQueries.findParentBuildings}
                                labelName={'name'}
                                valueName={'id'}
                                name="parent_id"
                                label={i18n.get('building.construction_project')}
                                withoutValue={values.id}
                                error={errors.parent_id}
                                touched={touched.parent_id}
                                onChange={selectedOption => {
                                    setFieldValue('parent_id', selectedOption ? selectedOption.value : '');
                                    setFieldValue('parent', selectedOption ? selectedOption.entity : '');
                                }}
                                isClearable={true}
                                defaultOptions={values.parent && [getSelectOptionEntity(values.parent)]}
                                onBlur={handleBlur}
                                value={values.parent && getSelectOptionEntity(values.parent)}
                                disabled={relation}
                            />
                        </Grid>
                    </React.Fragment>
                )}
                <Grid item xs={12} sm={6}>
                    <FormField
                        type="text"
                        name="name"
                        label={i18n.get('building.' + ( values.parent ? 'form.house_name' : 'construction_project') )}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.name}
                        error={errors.name}
                        touched={touched.name}
                        maxLength={255}
                        autoComplete={'off'}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FormAutocompleteGQL
                        query={costCenterQueries.allCostCenters}
                        labelName={'name'}
                        valueName={'id'}
                        name="cost_center_id"
                        label={i18n.get('building.cost_center')}
                        isClearable={true}
                        error={errors.cost_center_id}
                        touched={touched.cost_center_id}
                        defaultOptions
                        onChange={selectedOption => {
                            setFieldValue('cost_center_id', selectedOption ? selectedOption.value : '');
                            setFieldValue('cost_center', selectedOption && selectedOption.entity);
                        }}
                        mutateData={data => {
                            return data.number + ' - ' + data.name;
                        }}
                        onBlur={handleBlur}
                        value={values.cost_center && getSelectOptionEntity(
                            {id: values.cost_center_id, name: values.cost_center.number + ' - ' + values.cost_center.name}
                        )}
                        disabled={!!values.parent}
                    />
                </Grid>
            </Grid>

            <Grid container spacing={8}>
                <Grid item xs={12} sm={6} md={4}>
                    <FormField
                        type="text"
                        name="street"
                        label={i18n.get('building.form.street')}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.street}
                        error={errors.street}
                        touched={touched.street}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                    <FormField
                        type="text"
                        name="number"
                        label={i18n.get('building.form.number')}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.number}
                        error={errors.number}
                        touched={touched.number}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                    <FormField
                        type="text"
                        name="zip"
                        label={i18n.get('building.form.zip')}
                        maxLength="5"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.zip}
                        error={errors.zip}
                        touched={touched.zip}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <FormField
                        type="text"
                        name="city"
                        label={i18n.get('building.form.city')}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.city}
                        error={errors.city}
                        touched={touched.city}
                    />
                </Grid>
            </Grid>

            <Grid container spacing={8}>
                <Grid item xs={12} sm={6} md={3}>
                    <FormField
                        type="date"
                        name="acceptance"
                        label={i18n.get('building.form.acceptance')}
                        onChange={e => {
                            let value = e.target.value;
                            if(!!value) {
                                setFieldValue('acceptance',value);
                                calculateWarrantyEnd(value);
                            } else {
                                setFieldValue('acceptance',value);
                                setFieldValue('warranty_period_end','');
                                setWarrantyEnd('');
                            }
                            setAcceptance(value);
                        }}
                        onBlur={handleBlur}
                        error={errors.acceptance}
                        touched={touched.acceptance}
                        value={acceptance}
                        InputLabelProps={{shrink: true}}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <FormField
                        type="date"
                        name="warranty_period_end"
                        label={i18n.get('building.form.warranty_period_end')}
                        onChange={e => {
                            setFieldValue('warranty_period_end',e.target.value);
                            setWarrantyEnd(e.target.value);
                        }}
                        onBlur={handleBlur}
                        error={errors.warranty_period_end}
                        touched={touched.warranty_period_end}
                        value={warrantyEnd}
                        InputLabelProps={{shrink: true}}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <FormField
                        type="date"
                        name="start_of_construction"
                        label={i18n.get('building.form.start_of_construction')}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={errors.start_of_construction}
                        touched={touched.start_of_construction}
                        value={values.start_of_construction ? values.start_of_construction.substring(0, 10) : ''}
                        InputLabelProps={{shrink: true}}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}
                      container
                      spacing={0}
                      direction="row"
                      alignItems="flex-end"
                      justify="center"
                >
                    <FormCheck
                        name={"completed"}
                        value={values.completed}
                        label={i18n.get('building.form.completed')}
                        labelPlacement="end"
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                </Grid>
            </Grid>

            <Grid container spacing={8}>

                <Grid item xs={12} sm={6}>
                    <FormAutocompleteGQL
                        query={contactQueries.findBuilder}
                        labelName={'company_name'}
                        valueName={'id'}
                        name={'builder_id'}
                        label={ i18n.get('building.form.constructor') }
                        isClearable={true}
                        error={errors.builder_id}
                        touched={touched.builder_id}
                        onChange={selectedOption => {
                            if(selectedOption) {
                                setFieldValue('builder_id', selectedOption.value);
                                setFieldValue('builder', selectedOption.entity);
                            } else{
                                setFieldValue('builder_id', '');
                                setFieldValue('builder', null);
                            }
                            setContacts(selectedOption ? selectedOption.entity.children : []);
                        }}
                        onBlur={handleBlur}
                        placeholder={i18n.get('building.form.company_name')}
                        helperText={i18n.get('building.form.use_space')}
                        defaultValue={(values.builder)
                            && getSelectOptionEntity( values.builder,'company_name')
                        }
                        likeLeft
                    />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <FormSelect
                        type="text"
                        name="contact_id"
                        label={i18n.get('building.form.contacts') + (` (${contacts.length})` || '')}
                        onBlur={handleBlur}
                        error={errors.contact_id}
                        touched={touched.contact_id}
                        onChange={selectedOption => {
                            selectedOption
                                ? setFieldValue('contact_id', selectedOption.value)
                                : setFieldValue('contact_id', '');
                        }}
                        options={contacts.map(sub => {
                            return {
                                label: (
                                    <React.Fragment>
                                        {sub.first_name} {sub.last_name}
                                        {keyContacts.includes(sub.id + '')
                                            ? <span style={{marginLeft:5}}>({i18n.get('building.form.contact')})</span>
                                            : ''
                                        }
                                    </React.Fragment>
                                ),
                                value: sub.id,
                            }
                        })}
                        placeholder={contacts.length
                            ? i18n.get('building.please_select')
                            : i18n.get('building.no_contact_available')}
                        value={values.contact_id}
                        isClearable={true}
                    />
                </Grid>
            </Grid>

            {!values.parent &&(
                <Grid container spacing={8}>

                    <Grid item xs={12}>
                        <Divider className={classes.divider} />
                        <Typography variant={'h6'} color={'primary'}>
                            {i18n.get('building.form.site_management')}
                        </Typography>
                        <Typography variant={'caption'} style={{marginBottom:10,color:'rgba(0,0,0,0.5)'}}>
                            {i18n.get('building.form.hint')}
                        </Typography>
                    </Grid>


                    <Grid item xs={12} sm={4}>
                        <FormSelect
                            type={'text'}
                            name={'manager_id'}
                            label={i18n.get('building.form.manager')}
                            onChange={e => handleManagementChange(e ? e.value : null,'manager')}
                            options={ managementOptions }
                            isClearable={true}
                            value={personnel.manager_id}
                        />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <FormSelect
                            type={'text'}
                            name={'foreman_id'}
                            label={i18n.get('building.form.foreman')}
                            onChange={e => handleManagementChange(e ? e.value : null,'foreman')}
                            options={ managementOptions }
                            isClearable={true}
                            value={personnel.foreman_id}
                        />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <FormSelect
                            type={'text'}
                            name={'substitute_id'}
                            label={i18n.get('building.form.substitute')}
                            onChange={e => handleManagementChange(e ? e.value : null,'substitute')}
                            options={ managementOptions }
                            isClearable={true}
                            value={personnel.substitute_id}
                        />
                    </Grid>
                </Grid>
            )}

            {props.children}

            <Grid container spacing={24} justify="space-between"
                  alignItems="center"
                  style={{paddingTop: 8}}>
                <Grid item>
                    {values.id > 0 && (
                        <DeleteButton
                            disabled={isSubmitting}
                            href={`/construction/${values.id}`}
                            confirmMessage={i18n.get(
                                'contacts.confirm_message')}
                            successMessage={i18n.get(
                                'contacts.delete_success_message')}
                            handleSuccess={() => {
                                values.parent
                                    ? history.go(-1)
                                    : window.location.href = `/project`;
                            }}
                        />
                    )}
                </Grid>
                <Grid item>
                    { (!values.completed && values.id && !values.parent_id) && (
                        <Button size={"small"}
                                variant={"contained"}
                                href={`/construction/create?parent=${values.id}`}
                                color={"primary"}
                                style={{marginRight:10}}>
                            <Icon fontSize="small">add</Icon>
                            &nbsp;
                            {i18n.get('building.add_house')}
                        </Button>
                    )}
                    <Button size="small"
                            variant="contained"
                            color="primary"
                            type="submit"
                            disabled={isSubmitting}
                    >
                        <Icon fontSize="small">save</Icon>
                        &nbsp;
                        {i18n.get('building.actions.save')}
                    </Button>
                </Grid>
            </Grid>
        </React.Fragment>
    )
};

export default FormFields;
